import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

const slides = [
  {
    id: 1,
    tag: 'Tool Bags Collection',
    headline: ['PRECISION', 'CRAFTED FOR', 'THE FIELD'],
    highlight: 1,
    sub: 'Heavy-duty tool bags engineered for tradespeople who refuse to compromise. Built tough, organized smart.',
    cta: { label: 'Shop Tool Bags', path: '/products' },
    image: 'https://readdy.ai/api/search-image?query=professional%20heavy%20duty%20tool%20bag%20open%20showing%20organized%20compartments%20tools%20inside%20rugged%20canvas%20material%20dark%20workshop%20background%20dramatic%20side%20lighting%20product%20photography%20high%20end%20commercial&width=1920&height=1080&seq=hero-slide1&orientation=landscape',
    accent: 'from-stone-950/90 via-stone-900/60 to-transparent',
  },
  {
    id: 2,
    tag: 'Organizer Bags Series',
    headline: ['ORDER IN', 'EVERY', 'JOURNEY'],
    highlight: 2,
    sub: 'Sleek organizer bags that keep your essentials perfectly arranged — from the office to the outdoors.',
    cta: { label: 'Shop Organizers', path: '/products' },
    image: 'https://readdy.ai/api/search-image?query=premium%20organizer%20bag%20flat%20lay%20neatly%20arranged%20accessories%20cables%20gadgets%20minimalist%20clean%20background%20warm%20neutral%20tones%20luxury%20lifestyle%20product%20photography%20editorial%20style&width=1920&height=1080&seq=hero-slide2&orientation=landscape',
    accent: 'from-stone-950/85 via-stone-900/55 to-transparent',
  },
  {
    id: 3,
    tag: 'New 2026 Collection',
    headline: ['QUALITY', 'WITHOUT', 'COMPROMISE'],
    highlight: 0,
    sub: 'Over 15 years of manufacturing excellence. Trusted by professionals in 50+ countries worldwide.',
    cta: { label: 'Explore All', path: '/products' },
    image: 'https://readdy.ai/api/search-image?query=collection%20of%20premium%20bags%20tool%20bags%20organizer%20bags%20arranged%20on%20concrete%20surface%20industrial%20aesthetic%20moody%20cinematic%20lighting%20dark%20tones%20professional%20product%20display%20high%20quality%20photography&width=1920&height=1080&seq=hero-slide3&orientation=landscape',
    accent: 'from-stone-950/90 via-stone-900/60 to-transparent',
  },
];

const stats = [
  { value: '15+', label: 'Years of Excellence' },
  { value: '200+', label: 'Product Models' },
  { value: '50+', label: 'Countries Served' },
  { value: '10M+', label: 'Units Delivered' },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      goToSlide((current + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [current]);

  const goToSlide = (idx: number) => {
    if (idx === current) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setTransitioning(false);
    }, 400);
  };

  const slide = slides[current];

  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden bg-stone-950">
      {/* Background Image */}
      <div className={`absolute inset-0 transition-opacity duration-700 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
        <img
          src={slide.image}
          alt={slide.tag}
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-r ${slide.accent}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/20" />
      </div>

      {/* Decorative vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-amber-500 to-transparent opacity-60" />

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 w-full">
          <div className={`transition-all duration-700 ${visible && !transitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

            {/* Tag pill */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-amber-500" />
              <span className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase">
                {slide.tag}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white leading-none tracking-tight mb-8 max-w-4xl">
              {slide.headline.map((line, i) => (
                <span key={i} className={`block ${i === slide.highlight ? 'text-amber-400' : ''}`}>
                  {line}
                </span>
              ))}
            </h1>

            {/* Divider */}
            <div className="w-16 h-0.5 bg-amber-500 mb-6" />

            {/* Subtext */}
            <p className="text-white/65 text-base md:text-lg max-w-lg leading-relaxed mb-10 font-light">
              {slide.sub}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={slide.cta.path}
                className="inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 text-stone-950 px-8 py-4 rounded-none text-sm font-bold tracking-[0.2em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer group"
              >
                {slide.cta.label}
                <div className="w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 text-white hover:bg-white/10 px-8 py-4 rounded-none text-sm font-semibold tracking-[0.2em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Controls — right side */}
      <div className="absolute right-8 md:right-12 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`cursor-pointer transition-all duration-300 ${
              i === current
                ? 'w-1 h-10 bg-amber-500'
                : 'w-1 h-4 bg-white/30 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide number */}
      <div className="absolute right-8 md:right-12 bottom-24 z-20 text-right">
        <span className="text-amber-400 text-2xl font-black">0{current + 1}</span>
        <span className="text-white/30 text-sm"> / 0{slides.length}</span>
      </div>

      {/* Bottom Stats Bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 transition-all duration-1000 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div className="bg-stone-950/80 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
              {stats.map((stat) => (
                <div key={stat.label} className="py-5 px-6 text-center">
                  <div className="text-2xl md:text-3xl font-black text-amber-400">{stat.value}</div>
                  <div className="text-white/40 text-xs tracking-widest uppercase mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Brand name watermark */}
      <div className="absolute top-1/2 right-16 md:right-24 -translate-y-1/2 z-10 hidden lg:block pointer-events-none select-none">
        <span className="text-white/5 text-[120px] font-black tracking-widest uppercase leading-none">
          YUNXIN
        </span>
      </div>
    </section>
  );
}
